import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Palette, Check, Store, Type, SunMedium, RotateCcw } from "lucide-react";
import { THEME_MODES, themeModeLabel, getDeviceTheme, setDeviceTheme, getDisplayPrefs, setDisplayPrefs } from "@/utils/theme";
import { useLang } from "@/context/LangContext";

const TXT = {
  zh: { btn: "换肤", title: "界面主题", sub: "只影响这台设备，不影响其他收银机", follow: "跟随店铺默认", font: "字体大小", bright: "明暗度", clarity: "字体浓度", reset: "恢复默认", hint: "老板可在「系统设置」中设置全店默认主题" },
  es: { btn: "Tema", title: "Tema de pantalla", sub: "Solo cambia esta caja, no afecta a las demás", follow: "Igual que la tienda", font: "Tamaño de letra", bright: "Brillo", clarity: "Nitidez de letra", reset: "Restablecer", hint: "El jefe define el tema de toda la tienda en Configuración" },
  en: { btn: "Theme", title: "Display theme", sub: "Only affects this device", follow: "Follow store default", font: "Font size", bright: "Brightness", clarity: "Text boldness", reset: "Reset", hint: "The owner sets the store-wide theme in Settings" },
  pt: { btn: "Tema", title: "Tema da tela", sub: "Só muda este caixa", follow: "Seguir padrão da loja", font: "Tamanho da letra", bright: "Brilho", clarity: "Nitidez da letra", reset: "Restaurar", hint: "O dono define o tema padrão em Configurações" },
};

// 🎨 Per-device theme picker — cashier/tenant overrides the store default on THIS device
export const ThemeSwitcher = ({ block }) => {
  const { lang } = useLang();
  const x = TXT[lang] || TXT.es;
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(getDeviceTheme());
  const [prefs, setPrefs] = useState(getDisplayPrefs());

  const pick = (id) => { setDeviceTheme(id); setCur(id); };
  const upd = (k, v) => setPrefs(setDisplayPrefs({ [k]: Number(v) }));

  return (
    <>
      {block ? (
        <button onClick={() => setOpen(true)} data-testid="theme-switcher-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors">
          <Palette className="w-4 h-4 text-emerald-400" /> {x.btn}
        </button>
      ) : (
        <button onClick={() => setOpen(true)} data-testid="theme-switcher-btn" title={x.title}
          className="inline-flex items-center gap-1 h-9 w-9 md:h-7 md:w-auto justify-center rounded-md border border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700 text-xs px-2 transition-colors">
          <Palette className="w-4 h-4 md:w-3.5 md:h-3.5 text-emerald-400" />
          <span className="hidden lg:inline">{x.btn}</span>
        </button>
      )}
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[80] bg-black/65 backdrop-blur-[2px]" onClick={() => setOpen(false)} />
          <div className="fixed z-[81] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-xl max-h-[92vh] overflow-y-auto bg-slate-800 border border-slate-600/70 rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.55)] p-5" data-testid="theme-panel">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <Palette className="w-4.5 h-4.5 w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm tracking-wide">{x.title}</p>
                  <p className="text-slate-400 text-[11px] mt-0.5">{x.sub}</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white w-7 h-7 rounded-md hover:bg-slate-700 transition-colors" data-testid="theme-panel-close">✕</button>
            </div>

            <button onClick={() => pick("")} data-testid="theme-follow-store"
              className={`w-full mb-3 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-sm transition-all ${!cur ? "border-emerald-500/70 bg-emerald-500/10 text-emerald-300" : "border-slate-600/70 text-slate-300 hover:border-slate-400/70 hover:bg-slate-700/40"}`}>
              <Store className="w-4 h-4" /> {x.follow}
              {!cur && <Check className="w-4 h-4 ml-auto text-emerald-400" />}
            </button>

            <div className="grid grid-cols-3 gap-2.5">
              {THEME_MODES.map(m => (
                <button key={m.id} data-testid={`theme-opt-${m.id}`} onClick={() => pick(m.id)}
                  className={`group rounded-xl p-2.5 border transition-all text-left relative hover:-translate-y-0.5 hover:shadow-lg ${cur === m.id ? "border-emerald-500 ring-2 ring-emerald-500/25 shadow-lg" : "border-slate-600/60 hover:border-slate-400/70"}`}
                  style={{ backgroundColor: m.bg }}>
                  <div className="rounded-lg px-2 py-1.5 mb-2" style={{ backgroundColor: m.card }}>
                    <div className="h-1.5 w-3/4 rounded-full mb-1.5 bg-emerald-500/90" />
                    <div className="h-1 w-1/2 rounded-full" style={{ backgroundColor: m.fg, opacity: 0.45 }} />
                  </div>
                  <span className="text-[11px] font-semibold tracking-wide" style={{ color: m.fg }}>{themeModeLabel(m, lang)}</span>
                  {cur === m.id && (
                    <span className="absolute top-1.5 right-1.5 w-4.5 h-4.5 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow">
                      <Check className="w-3 h-3 text-white" />
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-slate-700/70 space-y-4">
              <div>
                <div className="flex justify-between items-center text-xs text-slate-300 mb-1.5">
                  <span className="flex items-center gap-1.5"><Type className="w-3.5 h-3.5 text-emerald-400" /> {x.font}</span>
                  <span className="font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded px-1.5 py-0.5">{prefs.font}%</span>
                </div>
                <input type="range" min="90" max="135" step="5" value={prefs.font}
                  onChange={e => upd("font", e.target.value)}
                  className="w-full accent-emerald-500 cursor-pointer" data-testid="font-scale-slider" />
              </div>
              <div>
                <div className="flex justify-between items-center text-xs text-slate-300 mb-1.5">
                  <span className="flex items-center gap-1.5"><SunMedium className="w-3.5 h-3.5 text-amber-400" /> {x.bright}</span>
                  <span className="font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded px-1.5 py-0.5">{prefs.bright > 0 ? `+${prefs.bright}` : prefs.bright}</span>
                </div>
                <input type="range" min="-30" max="30" step="5" value={prefs.bright}
                  onChange={e => upd("bright", e.target.value)}
                  className="w-full accent-emerald-500 cursor-pointer" data-testid="brightness-slider" />
              </div>
              <div>
                <div className="flex justify-between items-center text-xs text-slate-300 mb-1.5">
                  <span className="flex items-center gap-1.5"><Type className="w-3.5 h-3.5 text-sky-400" /> {x.clarity}</span>
                  <span className="font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded px-1.5 py-0.5">{prefs.clarity > 0 ? `+${prefs.clarity}` : "0"}</span>
                </div>
                <input type="range" min="0" max="4" step="1" value={prefs.clarity}
                  onChange={e => upd("clarity", e.target.value)}
                  className="w-full accent-emerald-500 cursor-pointer" data-testid="text-clarity-slider" />
              </div>
              <div className="flex items-center justify-between">
                <button onClick={() => setPrefs(setDisplayPrefs({ font: 100, bright: 0, clarity: 0 }))}
                  className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-slate-700/60 transition-colors" data-testid="display-reset">
                  <RotateCcw className="w-3.5 h-3.5" /> {x.reset}
                </button>
                <p className="text-[10px] text-slate-500">{x.hint}</p>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};
